import { useState, useEffect } from "react";

// Time values in milliseconds
const DAY = 24 * 60 * 60 * 1000;
const BACKOFF_SCHEDULE = [
  2 * DAY, // 1st dismiss
  7 * DAY, // 2nd dismiss
  30 * DAY, // 3rd dismiss
  180 * DAY, // 4th dismiss
];

export interface InstallPromptState {
  isPromptVisible: boolean;
  environment:
    | "android-chrome"
    | "ios-safari"
    | "ios-other"
    | "desktop"
    | "unknown"
    | "installed";
  dismissPrompt: () => void;
  promptInstall: () => Promise<void>;
  forceShowPrompt: () => void;
}

// Global variable to catch the event early if it fires before the hook mounts
let globalDeferredPrompt: any = null;
let globalPromptListenerReady = false;

if (typeof window !== "undefined" && !globalPromptListenerReady) {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    globalDeferredPrompt = e;
  });
  globalPromptListenerReady = true;
}

export const useInstallPrompt = (): InstallPromptState => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [environment, setEnvironment] =
    useState<InstallPromptState["environment"]>("unknown");

  // Define this outside useEffect or use useCallback if passed around,
  // but keeping it simple inside the hook:
  const checkAndShowPrompt = () => {
    try {
      const stored = localStorage.getItem("pwa_prompt_dismissal");
      if (stored) {
        const data = JSON.parse(stored);
        if (Date.now() < data.nextPromptDate) {
          return; // Still in backoff period
        }
      }
      setIsPromptVisible(true);
    } catch (err) {
      setIsPromptVisible(true);
    }
  };

  useEffect(() => {
    // 1. Detect if already installed / standalone
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone) {
      setEnvironment("installed");
      return;
    }

    // 2. Detect environment
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    const isMobile = isIOS || isAndroid || /windows phone/.test(ua);

    if (!isMobile) {
      setEnvironment("desktop");
      return;
    }

    if (isIOS) {
      if (/safari/.test(ua) && !/chrome|crios|fxios|opr|edgios/.test(ua)) {
        setEnvironment("ios-safari");
      } else {
        setEnvironment("ios-other");
      }
    } else if (isAndroid) {
      setEnvironment("android-chrome");
    }

    // 3. Handle setup based on platform
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      globalDeferredPrompt = e;
      setDeferredPrompt(e);
      checkAndShowPrompt();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // If it's iOS, there's no event, so we just run the check directly
    if (isIOS) {
      checkAndShowPrompt();
    } else if (isAndroid && globalDeferredPrompt) {
      // If the event already fired before this component mounted, catch it now
      setDeferredPrompt(globalDeferredPrompt);
      checkAndShowPrompt();
    }

    const handleForceShow = () => setIsPromptVisible(true);
    window.addEventListener("force-show-install-prompt", handleForceShow);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("force-show-install-prompt", handleForceShow);
    };
  }, []);

  const forceShowPrompt = () => {
    window.dispatchEvent(new Event("force-show-install-prompt"));
  };

  const dismissPrompt = () => {
    setIsPromptVisible(false);
    try {
      const stored = localStorage.getItem("pwa_prompt_dismissal");
      let dismissCount = 0;
      if (stored) {
        const data = JSON.parse(stored);
        dismissCount = data.dismissCount || 0;
      }

      const nextDelay = BACKOFF_SCHEDULE[dismissCount];

      if (nextDelay === undefined) {
        // >= 5 dismissals, never show again
        localStorage.setItem(
          "pwa_prompt_dismissal",
          JSON.stringify({
            dismissCount: dismissCount + 1,
            nextPromptDate: Date.now() + 100 * 365 * DAY, // ~100 years
          }),
        );
      } else {
        localStorage.setItem(
          "pwa_prompt_dismissal",
          JSON.stringify({
            dismissCount: dismissCount + 1,
            nextPromptDate: Date.now() + nextDelay,
          }),
        );
      }
    } catch (err) {
      console.error("Failed to save dismissal state", err);
    }
  };

  const promptInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setIsPromptVisible(false);
      setDeferredPrompt(null);
    } else {
      dismissPrompt();
    }
  };

  return {
    isPromptVisible,
    environment,
    dismissPrompt,
    promptInstall,
    forceShowPrompt,
  };
};
